import ProjectResolver from 'resolvers/RTMResolvers/ProjectResolver';

/**
 * Builds a service to access projects, and hold all of their business
 * logic.
 */
export default (() => {
  /**
   * @type {ProjectResolver}
   */
  let projectResolver;
  let isRegistered = false;

  const activeProjectSubscriptions = new Map();

  /**
   * Get list of projects belonging to a workspace
   * @param {string} workspaceId
   * @returns {array} [ProjectDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/project-dto)
   */
  const getProjectsList = async (workspaceId) => {
    const projects = await projectResolver.workspaceProjectsList({ workspaceId });
    return projects;
  };

  /**
   * Get project details
   * @param {string} projectId
   * @returns {object} [ProjectDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/project-dto)
   */
  const getProject = async (projectId) => {
    const project = await projectResolver.workspaceProjectGet({ projectId });
    return project;
  };

  /**
   * Get a list of all the Activities belonging to the project
   */
  const getProjectActivityList = async (projectId, configFields, publicationStatus) => {
    const activities = await projectResolver.projectActivityList({ projectId, fieldNames: configFields, publicationStatus });
    const activityList = activities.map((activity) => ({
      ...activity,
      configFields: activity.configFields.reduce(
        (acc, configField) => ({ ...acc, [configField.fieldName]: configField.fieldValue }),
        {}
      ),
    }));
    return activityList;
  };

  /**
   * Get a list of all the completed exports of the project
   */
  const getProjectExportList = async (projectId) => {
    const exportSummaries = await projectResolver.projectExportList({ projectId });
    return exportSummaries;
  };

  /**
   * Fetches a summary list of collaborators belonging to a project
   */
  const getProjectCollaboratorSummary = async (projectId) => {
    const collaborators = await projectResolver.workspaceProjectCollaboratorSummary({ projectId });
    return collaborators;
  };

  /**
   * Grant permission to an account over a project
   */
  const projectGrantPermission = async (accountOrTeamIds, collaboratorType, projectId, permissionLevel) => {
    const response = await projectResolver.workspaceProjectPermissionGrant({
      accountOrTeamIds,
      projectId,
      collaboratorType,
      permissionLevel,
    });
    return response;
  };

  /**
   * Revoke permission of an account over a project
   */
  const projectRevokePermission = async (accountOrTeamId, collaboratorType, projectId) => {
    const response = await projectResolver.workspaceProjectPermissionRevoke({
      accountOrTeamId,
      collaboratorType,
      projectId,
    });
    return response;
  };

  /**
   * Create or update a deployed activity.
   */
  const publishProjectActivity = async ({ cohortId, activityId, deploymentId }) => {
    const response = await projectResolver.projectActivityPublish({ activityId, cohortId, deploymentId });
    return response;
  };

  /**
   * Replace the name or config for a project.
   */
  const replaceProject = async (projectId, name, config) => {
    const response = await projectResolver.workspaceProjectReplace({ projectId, name, config });
    return response;
  };

  /**
   * Update the config for a project.
   */
  const updateProjectConfig = async (projectId, name, config) => {
    const response = await projectResolver.workspaceProjectReplace({ projectId, name, config });
    return response;
  };

  /**
   * Delete a project from a workspace
   */
  const deleteProject = async (projectId) => {
    const response = await projectResolver.workspaceProjectDelete({ projectId });
    return response;
  };

  /**
   * Move a project to another workspace
   */
  const moveProject = async (projectId, workspaceId) => {
    const response = await projectResolver.workspaceProjectMove({ projectId, workspaceId });
    return response;
  };

  /**
   * Duplicates an activity in projects
   * @param {string} projectId
   * @param {string} activityId
   * @param {string} [targetLanguage]
   * @returns {string} new activityId
   */
  const projectActivityDuplicate = async (projectId, activityId, targetLanguage = null) => {
    const newActivity = await projectResolver.projectActivityDuplicate(projectId, activityId, targetLanguage);

    return newActivity?.activityId;
  };

  const projectActivityMigrate = async (workspaceId, projectId, activityId) => {
    const newActivity = await projectResolver.projectActivityMigrate(workspaceId, projectId, activityId);
    return newActivity.exportId;
  };

  /**
   * Delete an activity from a project
   */
  const projectActivityDelete = async (projectId, activityId) => {
    const response = await projectResolver.workspaceProjectActivityDelete({ projectId, activityId });
    return response;
  };

  /**
   * Get project summary
   * @param {string} elementId
   * @param {string} elementType
   * @returns {object} [ProjectDTO](https://unsightly-rock.cloudvent.net/data-transfer-objects/project-dto)
   */
  const getProjectDetails = async (elementId, elementType) => {
    const projectDetails = await projectResolver.authorCoursewareProjectFind({ elementId, elementType });
    return projectDetails.error ? projectDetails.error : projectDetails.projectSummary;
  };

  /**
   * Request Ingestion process
   * @param {string} projectId
   * @param {string} configFields
   * @param {string} fileName
   * @param {string} courseName
   * @param {string} workspaceId
   * @param {string} rootElementId optional
   */
  const requestIngestion = async ( projectId, configFields, fileName, courseName, workspaceId, rootElementId ) => {
    const response = await projectResolver.projectIngestCreate({
      projectId,
      configFields,
      courseName,
      fileName,
      workspaceId,
      ...(rootElementId && rootElementId.length ? {rootElementId} : {}),
    });
    return response;
  };

  /**
   * Subscribe to project ingestions
   * @param {string} projectId
   * @param {function} listener
   * @returns {function} unsubscribe
   */

  const workspaceProjectSubscribe = async (projectId, listener) => {
    if (activeProjectSubscriptions.has(projectId)) {
      // eslint-disable-next-line no-console
      console.warn(`Already subscribed to project ${projectId}`);
      return activeProjectSubscriptions.get(projectId).unsubscribe;
    }

    activeProjectSubscriptions.set(projectId, { listener, unsubscribe: null });
    const unsubscribe = await projectResolver.workspaceProjectSubscribe(projectId, listener);
    activeProjectSubscriptions.set(projectId, { unsubscribe });

    return () => {
      unsubscribe();
      activeProjectSubscriptions.delete(projectId);
    };
  };

  /**
   * Unsubscribe to project ingestions
   * @param {string} projectId
   */

  const workspaceProjectUnsubscribe = async (projectId) => {
    const subscription = activeProjectSubscriptions.get(projectId);
    if (!subscription || !subscription.unsubscribe) {
      // eslint-disable-next-line no-console
      console.warn(`No active subscription to unsubscribe for project ${projectId}`);
      return;
    }
    subscription.unsubscribe();
    activeProjectSubscriptions.delete(projectId);
  };

  /**
   * Get Ingestion logs
   * @param {string} ingestionId
   */
  const getIngestionLogs = async (ingestionId) => {
    const response = await projectResolver.projectIngestLog({ ingestionId });
    return response;
  };

  /**
   * Start ingestion process on successful file upload
   * @param {string} ingestionId
   */
  const startIngestion = async (ingestionId, adapterType) => {
    const response = await projectResolver.projectIngestStart({
      ingestionId,
      adapterType,
    });
    return response;
  };

  /**
   * Update ingestion process
   * @param {string} ingestionId
   * @param {string} projectId
   * @param {string} status
   */
  const updateIngestion = async (ingestionId, projectId, status) => {
    const response = await projectResolver.projectIngestUpdate({
      ingestionId,
      projectId,
      status,
    });
    return response;
  };

  /**
   * Delete the ingestion
   * @param {string} ingestionId
   */
  const deleteIngestion = async (ingestionId) => {
    const response = await projectResolver.projectIngestDelete({
      ingestionId,
    });
    return response;
  };

  /**
   * Get ingestion data based on ingestionId
   * @param {string} ingestionId 
   * @returns 
   */
  const getIngestion = async (ingestionId) => {
    const response = await projectResolver.projectIngestGet({ ingestionId });
    if (response) return response.ingestion;
    return response;
  };

  /**
   * Get ingestion list based on rootElementId and projectId
   * @param {string} rootElementId
   * @param {string} projectId
   */
  const getRootImportList = async (rootElementId, projectId) => {
    const response = await projectResolver.projectIngestionRootGet({
      rootElementId,
      projectId,
    });
    return response;
  };

  return {
    isRegistered: () => isRegistered,

    register: (connectionProvider) => {
      isRegistered = true;
      projectResolver = new ProjectResolver(connectionProvider);
    },
    getProjectsList,
    getProject,
    getProjectActivityList,
    getProjectExportList,
    getProjectCollaboratorSummary,
    projectGrantPermission,
    projectRevokePermission,
    publishProjectActivity,
    deleteProject,
    moveProject,
    updateProjectConfig,
    replaceProject,
    projectActivityDuplicate,
    projectActivityMigrate,
    projectActivityDelete,
    getProjectDetails,
    startIngestion,
    workspaceProjectSubscribe,
    workspaceProjectUnsubscribe,
    getIngestionLogs,
    requestIngestion,
    updateIngestion,
    deleteIngestion,
    getIngestion,
    getRootImportList,
  };
})();
